import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { DEFAULT_ACCEPTED_FILES } from '~/common/constants/files';
import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { EmptyPage } from '~/eds/src/components/EmptyPage/EmptyPage';
import { FileInput } from '~/eds/src/components/FileInput/FileInput';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { TableContextType } from '~/enums/TableContextType';
import { AlgoStatusPanel } from '~/features/upload/AlgoStatusPanel';
import { UploadFile } from '~/features/upload/types';
import { UploadDocumentsPanel } from '~/features/upload/UploadDocumentsPanel';
import {
  formatUploadSize,
  getChipStatus,
  isFileUploading,
} from '~/features/upload/utils';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useTableSettings } from '~/hooks/useTableSettings';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { testIsSuperAdmin } from '~/utils/user';

import DEPRECATED_UploadPage from './DEPRECATED_index';

const Page = () => {
  const dispatch = useDispatch();
  const enableNewUploadPage = useFlag(FlagType.UploadPageNew);
  const user = useCurrentUser();
  const intl = useIntl();
  const isSuperAdmin = testIsSuperAdmin(user);

  const {
    tableSettings = {
      pageSize: 10,
    },
  } = useTableSettings(TableContextType.UploadFiles);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(tableSettings.pageSize);

  const { selectedFiles } = useSelector(
    slices.upload.selectors.selectActiveFile,
  );

  const {
    data = { count: 0, results: [] },
    isLoading,
    error: isErrorGettingUploadFiles,
  } = api.endpoints.getUploadFiles.useQuery({ page, pageSize });

  const handleRemoveFile = (
    removedFile: File,
    removedFileIndex: number | undefined,
  ) => {
    if (removedFileIndex !== undefined) {
      const updatedFiles = selectedFiles.filter(
        (_file, i) => removedFileIndex !== i,
      );
      dispatch(slices.upload.actions.setSelectedFiles(updatedFiles));
    }
  };

  useEffect(() => {
    if (tableSettings) {
      setPageSize(tableSettings.pageSize);
    }
  }, [tableSettings]);

  const onPaginate = useCallback(
    ({ pageIndex }: { pageIndex: number }) => {
      if (pageIndex !== page) {
        setPage(pageIndex);
      }
    },
    [page],
  );

  const onPageSizeChange = useCallback((pageSize: number) => {
    setPage(1);
    setPageSize(pageSize);
  }, []);

  if (!enableNewUploadPage) {
    return <DEPRECATED_UploadPage />;
  }

  const enableAlgoStatusColumn = isSuperAdmin
    ? [
        {
          cellType: 'link',
          key: 'algostatus',
          title: intl.formatMessage({
            id: 'Upload.algoStatusTitle',
            defaultMessage: 'AlgoStatus',
            description: 'Column title for algorithm status',
          }),
          mapCellProps: (d: UploadFile) => ({
            text: intl.formatMessage({
              id: 'Upload.showStatusText',
              defaultMessage: 'Show Status',
              description: 'Link text to show algorithm status',
            }),
            onClick: () => {
              dispatch(slices.upload.actions.setActiveFile(d));
            },
          }),
        },
      ]
    : [];

  const columns = [
    {
      key: 'name',
      cellType: 'filesystem',
      title: intl.formatMessage({
        id: 'Upload.documentNameTitle',
        defaultMessage: 'Document Name',
        description: 'Column title for document name',
      }),
      mapCellProps: (d: UploadFile) => ({
        //a link only when file has finished processing
        file: {
          id: `${d.id}`,
          name: `${d.name}${d.fileType}`,
          link: {
            pathname: `/document/${d.id}`,
            disabled: isFileUploading(d.status),
            tooltip: isFileUploading(d.status)
              ? intl.formatMessage(
                  {
                    id: 'Upload.documentNotProcessedTooltip',
                    defaultMessage: 'Document is not processed: {status}',
                    description:
                      'Tooltip when document is still processing. Example: status = "pending"',
                  },
                  { status: d.status },
                )
              : intl.formatMessage({
                  id: 'Upload.viewDocumentTooltip',
                  defaultMessage: 'View document in new tab',
                  description: 'Tooltip for viewing document',
                }),
          },
        },
      }),
      width: 'auto',
    },
    {
      key: 'size',
      cellType: 'number',
      title: intl.formatMessage({
        id: 'Upload.sizeTitle',
        defaultMessage: 'Size',
        description: 'Column title for file size',
      }),
      mapCellProps: (d: UploadFile) => ({
        number: formatUploadSize(d, intl).amount,
        options: {
          unit: formatUploadSize(d, intl).unit,
        },
      }),
      width: 'm',
    },
    {
      key: 'uploadDate',
      cellType: 'datetime',
      title: intl.formatMessage({
        id: 'Upload.uploadDateTitle',
        defaultMessage: 'Upload Date',
        description: 'Column title for upload date',
      }),

      mapCellProps: (d: UploadFile) => ({
        datetime: d.dateAdded,
        format: 'short_datetime',
      }),
      width: 'l',
    },
    {
      key: 'status',
      cellType: 'chips',
      title: intl.formatMessage({
        id: 'Upload.statusTitle',
        defaultMessage: 'Status',
        description: 'Column title for upload status',
      }),

      mapCellProps: (d: UploadFile) => ({
        chips: [getChipStatus(d, intl)],
      }),
    },
    ...enableAlgoStatusColumn,
  ];

  return (
    <PageLayout
      loadingContent={{
        isLoading,
        message: intl.formatMessage({
          id: 'Upload.loadingMessage',
          defaultMessage: 'Loading files…',
          description: 'Loading message while fetching files',
        }),
      }}
      title={intl.formatMessage({
        id: 'Upload.pageTitle',
        defaultMessage: 'Upload Documents',
        description: 'Page title for upload documents page',
      })}
    >
      <Layout preset="sections">
        <FileInput
          accept={DEFAULT_ACCEPTED_FILES}
          isMulti={true}
          name={intl.formatMessage({
            id: 'Upload.fileInputName',
            defaultMessage: 'Upload Files Dropzone',
            description: 'Name for file upload dropzone input',
          })}
          mode="card"
          onChange={(uploadedFiles) => {
            dispatch(
              slices.upload.actions.setSelectedFiles(uploadedFiles ?? []),
            );
          }}
          placeholder={intl.formatMessage({
            id: 'Upload.fileInputPlaceholder',
            defaultMessage: 'Drag document(s) here to upload them.',
            description: 'Placeholder text for file upload dropzone',
          })}
          value={null}
        />
        {data.count === 0 && !isLoading ? (
          <EmptyPage preset="no-uploaded-files" />
        ) : (
          <ContentContainer
            placeholderContent={
              isErrorGettingUploadFiles
                ? {
                    message: intl.formatMessage({
                      id: 'Upload.errorLoadingMessage',
                      defaultMessage:
                        'An error occurred while loading the upload logs…',
                      description:
                        'Error message when failed to load upload logs',
                    }),
                  }
                : undefined
            }
          >
            <PersistedTable
              columns={columns}
              context={TableContextType.UploadFiles}
              data={data?.results}
              isLoading={isLoading}
              name={intl.formatMessage({
                id: 'Upload.persistedTableName',
                defaultMessage: 'Upload Files',
                description: 'Name for persisted table of uploaded files',
              })}
              options={{
                enableExportXlsx: false,
                enablePageSizeSelect: false,
                enablePagination: true,
                enableManageColumns: false,
                enableSelectRows: false,
              }}
              state={{
                pageIndex: page,
                pageSize,
              }}
              onPageSizeChange={onPageSizeChange}
              onPaginate={onPaginate}
              totalCount={data?.count}
            />
          </ContentContainer>
        )}
        <AlgoStatusPanel />
        {selectedFiles.length > 0 && (
          <UploadDocumentsPanel onRemoveFile={handleRemoveFile} />
        )}
      </Layout>
    </PageLayout>
  );
};

export default Page;
